import {
    CheckCircleFilled,
    CloudUploadOutlined,
    DeleteOutlined,
    EditOutlined,
    ExportOutlined,
    LoadingOutlined,
    PlusOutlined,
    StopOutlined,
} from '@ant-design/icons';
import {Alert, Button, Card, Descriptions, Flex, Popconfirm, Space, Tag, theme, Tooltip, Typography} from 'antd';
import dayjs from 'dayjs';
import React, {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';

import {ApiQuery} from '../../../../../gen/api';
import {AdminEbayListingStatus, AdminEbaySellingLimitResp} from '../../../../../gen/api/models';
import {useNotification} from '../../../../../NotificationContext';
import {formatAgo, formatPrice, parseMoney} from '../../../dashboard/helpers/slowSelling';
import {EbayDraftDrawer} from './EbayDraftDrawer';
import {conditionLabel, LISTING_STATUS} from './ebayLabels';

const {Text} = Typography;

const errorMessage = (errors: Array<{message?: string}>) => errors?.[0]?.message;

const POLL_MS = 3000;

/** Statuses the worker is busy with; the card polls while in them. */
const BUSY: Partial<Record<AdminEbayListingStatus, string>> = {
    publishing: 'Uploading the photos and publishing; usually under a minute.',
    updating: 'Sending the changes to eBay; the listing stays live meanwhile.',
    ending: 'Ending the listing on eBay.',
};

/** Statuses whose content the admin can edit: drafts, failed ones, live (sends the changes) and ended (before a relist). */
const EDITABLE: AdminEbayListingStatus[] = ['draft', 'error', 'active', 'ended'];
/** Statuses that can be published: a draft, a failed one and an ended one (relist). */
const PUBLISHABLE: AdminEbayListingStatus[] = ['draft', 'error', 'ended'];

/** The selling limit changes only as we list and sell. */
const LIMIT_STALE_MS = 5 * 60_000;

/** What is left of the monthly selling limit, and whether this listing would go over it. */
function sellingLimitNote(
    limit: AdminEbaySellingLimitResp | undefined,
    price: number | undefined
): {text: string; over: boolean} | undefined {
    // The API sends null for "no cap"; the generated type says undefined.
    const items = limit?.quantityRemaining ?? undefined;
    const amount = parseMoney(limit?.amountRemaining);
    if (items === undefined && amount === undefined) {
        return undefined;
    }
    const left = [
        items !== undefined && `${items.toLocaleString('en-US')} items`,
        amount !== undefined && formatPrice(amount),
    ].filter(Boolean);
    const over = (items !== undefined && items < 1) || (amount !== undefined && price !== undefined && amount < price);
    return {
        text: over
            ? `Over the monthly selling limit (${left.join(', ')} left): eBay will refuse it.`
            : `Monthly selling limit left: ${left.join(', ')}.`,
        over,
    };
}

const formatTime = (value?: string | Date) => (value ? dayjs(value).format('MM/DD/YYYY HH:mm') : '');

/**
 * The product's eBay listing: its draft or live listing, what publishing still needs, and the actions.
 * onWorkerDone: the worker finished publishing, updating or ending, and with it the product's eBay marketplace row.
 */
export const EbayListingCard: React.FC<{productId: string; onWorkerDone?: () => void}> = ({
    productId,
    onWorkerDone,
}) => {
    const {token} = theme.useToken();
    const notify = useNotification();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const listingQuery = ApiQuery.useAdminEbayListingQuery(
        {id: productId},
        // The worker changes the listing in the background: poll until it is done.
        {staleTime: 0, refetchInterval: (data) => (data && BUSY[data.status] ? POLL_MS : false)}
    );
    const listing = listingQuery.data;
    const lastStatus = useRef<AdminEbayListingStatus>();
    useEffect(() => {
        const previous = lastStatus.current;
        lastStatus.current = listing?.status;
        if (previous && BUSY[previous] && listing && !BUSY[listing.status]) {
            onWorkerDone?.();
        }
    }, [listing?.status]);

    const onMutationError = (title: string) => (errors: Array<{message?: string}>) =>
        notify.openNotificationWithIcon('error', title, errorMessage(errors));
    const deleteMutation = ApiQuery.useAdminEbayDeleteDraftMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('info', 'eBay draft deleted');
            listingQuery.refetch();
        },
        onError: onMutationError('Failed to delete the draft'),
    });
    const publishMutation = ApiQuery.useAdminEbayPublishMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon(
                'info',
                'Publishing on eBay',
                'Uploading the photos and publishing the offer.'
            );
            listingQuery.refetch();
        },
        onError: onMutationError('Cannot publish'),
    });
    const endMutation = ApiQuery.useAdminEbayEndMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('info', 'Ending the eBay listing');
            listingQuery.refetch();
        },
        onError: onMutationError('Cannot end the listing'),
    });

    const status = listing ? LISTING_STATUS[listing.status] : undefined;
    const busy = listing ? BUSY[listing.status] : undefined;
    const editable = !listing || EDITABLE.includes(listing.status);
    const canPublish = !!listing?.saved && listing.problems.length === 0 && PUBLISHABLE.includes(listing.status);
    // Never published: the draft can be thrown away.
    const deletable =
        !!listing?.saved && !listing.listingId && (listing.status === 'draft' || listing.status === 'error');
    const sandbox = listing?.environment === 'sandbox' ? ' (sandbox)' : '';
    // What is left of the monthly selling limit, shown when confirming a publish.
    const limitQuery = ApiQuery.useAdminEbaySellingLimitQuery({enabled: canPublish, staleTime: LIMIT_STALE_MS});
    const limitNote = sellingLimitNote(limitQuery.data, parseMoney(listing?.price));

    const publishLabel =
        listing?.status === 'ended' ? 'Relist' : listing?.status === 'error' ? 'Publish again' : 'Publish';
    const editLabel = !listing?.saved
        ? 'Create draft'
        : listing.status === 'draft' || listing.status === 'error'
          ? 'Edit draft'
          : 'Edit';

    return (
        <Card
            title={
                <Space>
                    eBay listing
                    {listing?.environment === 'sandbox' && <Tag color="orange">SANDBOX</Tag>}
                    {listing?.saved && status && <Tag color={status.color}>{status.label}</Tag>}
                </Space>
            }
            size="small"
            style={{marginBottom: 16}}
            loading={listingQuery.isLoading}
            extra={
                listing &&
                editable && (
                    <Space>
                        {deletable && (
                            <Popconfirm
                                title="Delete the eBay draft?"
                                okText="Delete"
                                okButtonProps={{danger: true}}
                                onConfirm={() => deleteMutation.mutate({productId})}
                            >
                                <Button
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    loading={deleteMutation.isLoading}
                                />
                            </Popconfirm>
                        )}
                        {listing.status === 'active' && (
                            <Popconfirm
                                title={`End the eBay listing${sandbox}?`}
                                description="Buyers will not see it any more; it can be relisted later."
                                okText="End listing"
                                okButtonProps={{danger: true}}
                                onConfirm={() => endMutation.mutate({id: productId})}
                            >
                                <Button
                                    size="small"
                                    danger
                                    icon={<StopOutlined />}
                                    loading={endMutation.isLoading}
                                >
                                    End listing
                                </Button>
                            </Popconfirm>
                        )}
                        <Button
                            size="small"
                            type={listing.saved ? 'default' : 'primary'}
                            icon={listing.saved ? <EditOutlined /> : <PlusOutlined />}
                            onClick={() => setDrawerOpen(true)}
                        >
                            {editLabel}
                        </Button>
                        {canPublish && (
                            <Popconfirm
                                title={`${publishLabel} on eBay${sandbox}?`}
                                description={
                                    <Flex vertical>
                                        <span>
                                            {formatPrice(listing.price)}, {listing.imagesCount} photos.
                                        </span>
                                        {limitNote && (
                                            <Text type={limitNote.over ? 'danger' : 'secondary'}>{limitNote.text}</Text>
                                        )}
                                    </Flex>
                                }
                                okText={publishLabel}
                                onConfirm={() => publishMutation.mutate({id: productId})}
                            >
                                <Button
                                    size="small"
                                    type="primary"
                                    icon={<CloudUploadOutlined />}
                                    loading={publishMutation.isLoading}
                                >
                                    {publishLabel}
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                )
            }
        >
            {listingQuery.isError && (
                <Alert
                    type={errorMessage(listingQuery.error)?.includes('not configured') ? 'info' : 'error'}
                    showIcon
                    message={errorMessage(listingQuery.error) ?? 'Failed to load the eBay listing'}
                    description={
                        <span>
                            See the <Link to="/ebay">eBay page</Link>.
                        </span>
                    }
                />
            )}

            {listing && !listing.saved && (
                <Text type="secondary">
                    Not on eBay from the admin yet. The draft is filled from the product; choose a category and check
                    the item specifics.
                </Text>
            )}

            {listing?.saved && (
                <Descriptions
                    column={1}
                    size="small"
                    items={[
                        {key: 'title', label: 'Title', children: listing.title},
                        {
                            key: 'category',
                            label: 'Category',
                            children: listing.categoryName ?? <Text type="secondary">not chosen</Text>,
                        },
                        {key: 'condition', label: 'Condition', children: conditionLabel(listing.condition)},
                        {
                            key: 'price',
                            label: 'Price',
                            children: (
                                <span>
                                    {formatPrice(listing.price)}
                                    <Text type="secondary">
                                        {' '}
                                        ·{' '}
                                        {Number(listing.buyerShipping) > 0
                                            ? `buyer pays ${formatPrice(listing.buyerShipping)} shipping`
                                            : 'free shipping'}
                                    </Text>
                                </span>
                            ),
                        },
                        {
                            key: 'updated',
                            label: 'Saved',
                            children: listing.updatedAt ? (
                                <Tooltip title={formatTime(listing.updatedAt)}>
                                    {formatAgo(String(listing.updatedAt))} by {listing.updatedByName}
                                </Tooltip>
                            ) : (
                                '—'
                            ),
                        },
                    ]}
                />
            )}

            {busy && (
                <Flex
                    gap={6}
                    align="center"
                    style={{marginTop: 8}}
                >
                    <LoadingOutlined />
                    <Text>{busy}</Text>
                </Flex>
            )}

            {/* While relisting, the old item number is not live any more. */}
            {listing?.listingUrl && listing.status !== 'publishing' && (
                <Flex
                    gap={6}
                    align="center"
                    wrap
                    style={{marginTop: 8}}
                >
                    {listing.status === 'sold' ? (
                        <Text>Sold on eBay {formatTime(listing.endedAt)}</Text>
                    ) : listing.status === 'ended' ? (
                        <Text type="secondary">Ended on eBay {formatTime(listing.endedAt)}</Text>
                    ) : (
                        <>
                            <CheckCircleFilled style={{color: token.colorSuccess}} />
                            <Text>
                                Live on eBay
                                {listing.publishedAt && (
                                    <Text type="secondary"> since {formatTime(listing.publishedAt)}</Text>
                                )}
                            </Text>
                        </>
                    )}
                    <a
                        href={listing.listingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <ExportOutlined /> item {listing.listingId}
                    </a>
                </Flex>
            )}

            {listing?.lastError && (
                <Alert
                    type="error"
                    showIcon
                    style={{marginTop: 8}}
                    message={
                        listing.status === 'error'
                            ? 'eBay refused the listing'
                            : listing.status === 'sold'
                              ? 'Check the sale'
                              : 'eBay did not take the last change; the listing is as before'
                    }
                    description={listing.lastError}
                />
            )}

            {listing &&
                listing.problems.length > 0 &&
                listing.status !== 'active' &&
                listing.status !== 'sold' &&
                !busy && (
                    <Alert
                        type="warning"
                        style={{marginTop: 8}}
                        message={listing.status === 'ended' ? 'Before relisting' : 'Before publishing'}
                        description={
                            <ul style={{margin: 0, paddingLeft: 18}}>
                                {listing.problems.map((problem) => (
                                    <li key={problem}>{problem}</li>
                                ))}
                            </ul>
                        }
                    />
                )}
            {listing?.saved && listing.problems.length === 0 && listing.status === 'draft' && (
                <Flex
                    gap={6}
                    align="center"
                    style={{marginTop: 8}}
                >
                    <CheckCircleFilled style={{color: token.colorSuccess}} />
                    <Text>Ready to publish.</Text>
                </Flex>
            )}

            {listing && (
                <EbayDraftDrawer
                    productId={productId}
                    listing={listing}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    onSaved={() => {
                        setDrawerOpen(false);
                        listingQuery.refetch();
                    }}
                />
            )}
        </Card>
    );
};
